import React from "react";
import { Dropdown, Form } from "react-bootstrap";
import styles from "./msp-accounts-table-row.module.scss";
import { RowDropdown } from "./row-dropdown";

const getAvatarCircleClass = (bgColor) => {
  if (bgColor === "bg-additional-violet-50") return styles.avatarCircleViolet;
  if (bgColor === "bg-support-alert-50") return styles.avatarCircleRed;
  return styles.avatarCircleViolet;
};

const getTagClass = (bgColor) => {
  if (bgColor === "bg-support-alert-0") return styles.tagAlert;
  if (bgColor === "bg-brand-purple-0") return styles.tagPurple;
  return styles.tagAlert;
};

const getTagLabelClass = (textColor) => {
  if (textColor === "text-support-alert-50") return styles.tagLabelAlert;
  if (textColor === "text-brand-purple-50") return styles.tagLabelPurple;
  return styles.tagLabelAlert;
};

export const MspAccountsTableRow = ({ 
  rowData, 
  isArchived = false, 
  isSelected = false, 
  onSelect, 
  showCheckbox = false,
  isDistributorView = false,
  onRowClick,
  rowIndex
}) => {
  const [avatarError, setAvatarError] = React.useState(false);
  
  if (!rowData) {
    return null;
  }

  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    if (onSelect) {
      onSelect(e.target.checked, false);
    }
  };

  const handleRowClick = (e) => {
    // Don't trigger selection if clicking on dropdown, checkbox, or links
    if (
      e.target.closest('[role="menu"]') || 
      e.target.closest('.dropdown') || 
      e.target.closest('input[type="checkbox"]') ||
      e.target.closest('button') ||
      e.target.closest('a')
    ) {
      return;
    }

    // If in distributor view, navigate to overview page
    if (isDistributorView && onRowClick) {
      const mspAccountId = rowData.id || rowData.objId_Customer || rowIndex?.toString() || rowData.companyName;
      onRowClick(mspAccountId);
      return;
    }

    if (e.shiftKey && onSelect) {
      onSelect(!isSelected, true);
    } else if (onSelect && !e.target.closest('input[type="checkbox"]')) {
      // Regular click toggles selection when checkboxes are visible
      if (showCheckbox) {
        onSelect(!isSelected, false);
      }
    }
  };

  const rowClassName = showCheckbox 
    ? `${styles.row} ${styles.rowWithCheckbox}`
    : styles.row;

  return (
    <div className={rowClassName} onClick={handleRowClick}>
      {showCheckbox && (
        <div className={styles.checkboxColumn}>
          <div className={styles.checkboxCell}>
            <div className={styles.checkboxContainer}>
              <Form.Group className={styles.checkboxGroup}>
                <Form.Check
                  type="checkbox"
                  checked={isSelected}
                  onChange={handleCheckboxChange}
                  onClick={(e) => e.stopPropagation()}
                  className={styles.checkbox}
                />
              </Form.Group>
            </div>
          </div>
        </div>
      )}
      <div className={styles.companyContainer}>
        {rowData.companyAvatar && !avatarError ? (
          <div className={styles.avatarContainer}>
            <img
              className={styles.avatar}
              src={rowData.companyAvatar}
              alt={rowData.companyName}
              onError={() => setAvatarError(true)}
            />
          </div>
        ) : (
          <div className={`${styles.avatarCircle} ${styles.avatarCircleViolet}`}>
            <div className={styles.avatarInitial}>
              {rowData.companyName.charAt(0).toUpperCase()}
            </div>
          </div>
        )}

        <div className={styles.companyNameContainer}>
          <div className={styles.companyName}>
            {rowData.companyName}
          </div>
        </div>
      </div>

      <div className={styles.locationCell}>
        <div className={styles.locationText}>
          {rowData.country}
        </div>
      </div>

      <div className={styles.contactCell}>
        <div
          className={`${styles.avatarCircle} ${getAvatarCircleClass(rowData.primaryContact.bgColor)}`}
        >
          <div className={styles.avatarInitial}>
            {rowData.primaryContact.initial}
          </div>
        </div>

        <div className={styles.contactInfo}>
          <div className={styles.contactNameContainer}>
            <div className={styles.contactName}>
              {rowData.primaryContact.name}
            </div>
          </div>

          <div className={styles.contactEmail}>
            {rowData.primaryContact.email}
          </div>
        </div>
      </div>

      <div className={styles.salesRepCell}>
        <div
          className={`${styles.avatarCircle} ${getAvatarCircleClass(rowData.accountManager.bgColor)}`}
        >
          <div className={styles.avatarInitial}>
            {rowData.accountManager.initial}
          </div>
        </div>

        <div className={styles.contactInfo}>
          <div className={styles.contactNameContainer}>
            <div className={styles.contactName}>
              {rowData.accountManager.name}
            </div>
          </div>

          <div className={styles.contactEmail}>
            {rowData.accountManager.email}
          </div>
        </div>
      </div>

      <div className={styles.dateCell}>
        <div className={styles.dateText}>
          {rowData.createdDate}
        </div>
      </div>

      <div className={styles.dateCell}>
        <div className={styles.dateText}>
          {rowData.lastActivity}
        </div>
      </div>

      <div className={styles.subscriptionCell}>
        <div
          className={`${styles.subscriptionBadge} ${styles.subscriptionBadgeSuccess}`}
        >
          <div className={styles.subscriptionBadgeInner}>
            <div
              className={`${styles.subscriptionLabel} ${styles.subscriptionLabelSuccess}`}
            >
              {rowData.subscriptionStatus.label}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.trialDaysCell}>
        <div className={styles.trialDaysText}>
          {rowData.trialDays}
        </div>
      </div>

      <div className={styles.renewalDateCell}>
        <div className={styles.renewalDateText}>
          {rowData.renewalDate}
        </div>
      </div>

      <div className={styles.autoRenewCell}>
        <div className={styles.autoRenewText}>
          {rowData.autoRenew}
        </div>
      </div>

      <div className={styles.paymentMethodCell}>
        <div className={styles.paymentMethodText}>
          {rowData.paymentMethod}
        </div>
      </div>

      <div className={styles.tagsCell}>
        {rowData.tags && rowData.tags.length > 0 ? (
          rowData.tags.map((tag, index) => (
            <div
              key={index}
              className={`${styles.tag} ${getTagClass(tag.bgColor)}`}
            >
              <div className={styles.tagInner}>
                <div
                  className={`${styles.tagLabel} ${getTagLabelClass(tag.textColor)}`}
                >
                  {tag.label}
                </div>
              </div>
            </div>
          ))
        ) : null}
      </div>

      <div className={styles.statusCell}>
        {rowData.dealStatus ? (
          <div
            className={`${styles.statusBadge} ${styles.statusBadgeSuccess}`}
          >
            <div className={styles.statusIcon}>
              <img
                className={styles.statusIconImg}
                alt="Base circle xs"
                src={rowData.dealStatus.icon}
              />
            </div>

            <div className={styles.statusLabel}>
              <div
                className={`${styles.statusLabelText} ${styles.statusLabelTextSuccess}`}
              >
                {rowData.dealStatus.label}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <div className={styles.numberCell}>
        <div className={styles.numberText}>
          {rowData.users}
        </div>
      </div>

      <div className={styles.numberCell}>
        <div className={styles.numberText}>
          {rowData.licenses}
        </div>
      </div>

      <div className={styles.numberCell}>
        <div className={styles.numberText}>
          {rowData.devices}
        </div>
      </div>

      <div className={styles.numberCell}>
        <div className={styles.numberText}>
          {rowData.tickets}
        </div>
      </div>

      <div className={styles.integrationsCell}>
        {rowData.integrations.map((integration, index) => (
          <div
            key={index}
            className={styles.integrationItem}
          >
            <div
              className={styles.integrationIcon}
              style={{ backgroundImage: `url(${integration.icon})` }}
            />

            <div className={styles.integrationName}>
              {integration.name}
            </div>
          </div>
        ))}
      </div>

      <div className={styles.numberCell}>
        <div className={styles.numberText}>
          {rowData.mrr}
        </div>
      </div>

      <div className={styles.numberCell}>
        <div className={styles.numberText}>
          {rowData.arr}
        </div>
      </div>

      <div className={styles.numberCell}>
        <div className={styles.numberText}>
          {rowData.nps}
        </div>
      </div>

      <div className={styles.moreButton}>
        <Dropdown align="end">
          <Dropdown.Toggle className={styles.dropdownToggle} variant="link">
            <img className={styles.moreButtonImg} alt="" src="/images/horizontal-dots.svg" />
          </Dropdown.Toggle>
          <Dropdown.Menu className={styles.dropdownMenuWrapper}>
            <RowDropdown isArchived={isArchived} companyName={rowData.companyName} />
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </div>
  );
};